/* eslint-disable complexity */
import { StateStoreOrVoid } from '../../../../core/types/application-state';
import { DataLoader } from '../../../../core/types/data-loader';
import { withDefault, removeCommasAndParseFloat } from '../../../../core/util';
import { createNewReturnJourneyProfile } from '../../../return-journey/loaders';
import { getExistingApplications } from '../../../shared/services/profile-service';
import {
  consumerTypes,
  fundingTypes,
  investmentMethods,
  setDefaultedProfileData
} from '../../../shared/utils';
import { analyticsDataLayer } from '../../../../analytics/analytics-helper';
import { parseIClassFunds } from '../../../shared/summit.utils';
import { getModalShownAndResult } from '../../../shared/loaders';

export const commitProfileData: DataLoader = async ({
  chosen,
  inputs,
  store
}): Promise<StateStoreOrVoid> => {
  if (chosen === 'skipLogin') {
    return {};
  }

  const shoppingFunds = chosen;
  const { amount } = inputs;
  const { accountType } = store;

  // account for defaulted input values not persisting for customers with existing data
  const defaultedProfileData = setDefaultedProfileData(inputs, store);
  const existingApplications = await getExistingApplications(
    defaultedProfileData.primaryEmailAddress,
    accountType
  );

  // filter applications which does not belong to 'FRONT_OFFICE_NASU'
  const filterExistingApps =
    Array.isArray(existingApplications) &&
    existingApplications?.filter(
      appID => appID.consumerName !== consumerTypes.FONASU
    );

  // get Profile ID of first application
  const existingID = filterExistingApps?.[0]?.applicationID;

  const storeToCommit = {
    accountType,
    amount: removeCommasAndParseFloat(withDefault(amount, store?.amount)),
    fundingMethodType: fundingTypes.FUND_LATER,
    app: {
      customer: {
        name: {
          firstName: defaultedProfileData.firstName,
          lastName: defaultedProfileData.lastName
        },
        primaryEmailAddress: defaultedProfileData.primaryEmailAddress,
        dob: defaultedProfileData.dob
      }
    },
    ...(shoppingFunds && { loadedData: { shoppingFunds } })
  };

  if (!existingID) return createNewReturnJourneyProfile(storeToCommit);

  return {
    ...storeToCommit,
    app: { ...storeToCommit.app, profileApplicationID: existingID }
  };
};

export const commitMoneyMarketFund: DataLoader = async ({
  store: { amount },
  chosen
}) => {
  if (chosen === 'viewAllMoneyMarketFunds') return {};

  const { investmentMethod, fund } = chosen;
  const { ticker, trpCode, correspondingFund } = fund;

  const newInvestments = [
    {
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount: 0
      }
    }
  ];

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    amount,
    app: { newInvestments, investmentMethod },
    loadedData: { moneyMarketData: { ...fund } },
    chosenInvestments: [{ ...fund, amount: 0, correspondingFund }]
  };
};

export const commitNewInvestmentsBYOP: DataLoader = ({
  store,
  chosen,
  result
}) => {
  const { modalShown, updateToIClass } = getModalShownAndResult(
    result,
    store?.updateToIClassModalShown
  );
  const chosenInvestments = parseIClassFunds(
    chosen,
    updateToIClass,
    store?.summitData
  );

  const newInvestments = chosenInvestments.map(
    ({ ticker, trpCode, unlockedFund }) => ({
      ticker,
      trpCode,
      fundingAmount: 0,
      unlockedFund: updateToIClass || unlockedFund
    })
  );

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    app: {
      newInvestments,
      investmentMethod: investmentMethods.BYOP
    },
    amount: store.amount,
    chosenInvestments,
    updateToIClassModalShown: modalShown
  };
};
